import React, { useState, useEffect } from "react";
import { Box, Button, useTheme, IconButton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import axios from "axios";
import { useColourMode } from "../contexts/ColourContext";
import { ReactComponent as SearchIcon } from "../images/svg/search.svg";
import { prodApi } from "../data/api";

const dataUrl = prodApi.data;
const url = `${dataUrl}/guestrooms`;

const GuestRoomCarousel = ({ userId, guestrooms, onUpdate, onDelete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState({
    type: "",
    price: "",
    image: null,
  });
  const colourTheme = useTheme();
  const { accentColour } = useColourMode();

  useEffect(() => {
    setCurrentIndex(0);
  }, [guestrooms]);

  useEffect(() => {
    if (guestrooms.length > 0 && guestrooms[currentIndex]) {
      setFormData({
        type: guestrooms[currentIndex].type || "",
        price: guestrooms[currentIndex].price || "",
        image: null,
      });
    }
  }, [currentIndex, guestrooms]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? guestrooms.length - 1 : prev - 1));
    setIsExpanded(false);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === guestrooms.length - 1 ? 0 : prev + 1));
    setIsExpanded(false);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    const maxSize = 512 * 1024; // 512KB

    const compressImage = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;

          img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const maxWidth = 800;
            const maxHeight = 800;
            let width = img.width;
            let height = img.height;

            if (width > maxWidth || height > maxHeight) {
              const ratio = Math.min(maxWidth / width, maxHeight / height);
              width *= ratio;
              height *= ratio;
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
              (blob) => {
                if (blob) {
                  if (blob.size > maxSize) {
                    reject(new Error("Image is too large after compression"));
                  } else {
                    const compressedFile = new File([blob], file.name, {
                      type: "image/jpeg",
                      lastModified: Date.now(),
                    });
                    resolve(compressedFile);
                  }
                } else {
                  reject(new Error("Compression failed"));
                }
              },
              "image/jpeg",
              0.6
            );
          };

          img.onerror = () => reject(new Error("Failed to load image"));
        };

        reader.onerror = () => reject(new Error("Failed to read file"));
      });
    };

    if (file.size > maxSize) {
      compressImage(file)
        .then((compressedFile) => {
          setFormData({ ...formData, image: compressedFile });
          alert("Image was compressed successfully.");
        })
        .catch((err) => {
          console.error("Image compression failed:", err);
          alert(
            "Image is too large. Please select an image smaller than 512KB."
          );
          e.target.value = "";
        });
    } else {
      setFormData({ ...formData, image: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authorization token is missing.");
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append("type", formData.type);
      formDataToSend.append("price", formData.price);
      if (formData.image) {
        formDataToSend.append("roomImage", formData.image);
      }

      await axios.put(
        `${url}/${userId}/${guestrooms[currentIndex].typeId}`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setIsExpanded(false);
      onUpdate();
    } catch (error) {
      console.error("Error updating guestroom:", error);
      alert("Failed to update guestroom. Please try again.");
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authorization token is missing.");
        return;
      }

      const confirmed = window.confirm(
        "Are you sure you want to delete this guestroom?"
      );
      if (confirmed) {
        await axios.delete(
          `${url}/${userId}/${guestrooms[currentIndex].typeId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        onDelete();
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error("Error deleting guestroom:", error);
    }
  };

  if (guestrooms.length === 0) {
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <Typography>No guestrooms found</Typography>
      </Box>
    );
  }

  const currentExhibit = guestrooms[currentIndex];

  if (!currentExhibit) {
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <Typography>No guestroom to display</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: "800px",
        mx: "auto",
        marginTop: "2rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          mb: 2,
        }}
      >
        <IconButton
          onClick={handlePrev}
          sx={{
            color: colourTheme.palette.text.primary,
            "&:hover": { color: accentColour },
          }}
        >
          <ChevronLeft fontSize="large" />
        </IconButton>

        <Box
          onClick={toggleExpand}
          sx={{
            width: "100%",
            maxWidth: "600px",
            position: "relative",
            cursor: "pointer",
            overflow: "hidden",
            borderRadius: "8px",
            boxShadow: 3,
          }}
        >
          <img
            src={currentExhibit.imagePath}
            alt={currentExhibit.type}
            style={{
              width: "100%",
              height: "100%",
              aspectRatio: 1,
              objectFit: "cover",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: "rgba(0,0,0,0.9)",
              color: "white",
              p: 2,
              zIndex: 1002,
              textAlign: "center",
            }}
          >
            <Box>{currentExhibit.type}</Box>
          </Box>
        </Box>

        <IconButton
          onClick={handleNext}
          sx={{
            color: colourTheme.palette.text.primary,
            "&:hover": { color: accentColour },
          }}
        >
          <ChevronRight fontSize="large" />
        </IconButton>
      </Box>

      {isExpanded && (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: "100%",
            maxWidth: "600px",
            bgcolor: colourTheme.palette.background.paper,
            p: 3,
            borderRadius: "8px",
            boxShadow: 3,
            mt: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Edit Guest Room
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Type:
            </Typography>
            <input
              type="text"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              style={{
                width: "100%",
                padding: "0.5rem",
                borderRadius: "4px",
                border: `1px solid ${colourTheme.palette.divider}`,
                backgroundColor: colourTheme.palette.background.default,
                color: colourTheme.palette.text.primary,
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Price:
            </Typography>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              style={{
                width: "100%",
                padding: "0.5rem",
                borderRadius: "4px",
                border: `1px solid ${colourTheme.palette.divider}`,
                backgroundColor: colourTheme.palette.background.default,
                color: colourTheme.palette.text.primary,
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Update Image:
            </Typography>
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleImageChange}
              style={{
                width: "100%",
                padding: "0.5rem",
                borderRadius: "4px",
                border: `1px solid ${colourTheme.palette.divider}`,
                backgroundColor: colourTheme.palette.background.default,
                color: colourTheme.palette.text.primary,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button type="submit" variant="contained" color="primary" fullWidth>
              Save Changes
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
            >
              Delete Room
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
        {guestrooms.map((_, index) => (
          <Box
            key={index}
            onClick={() => {
              setCurrentIndex(index);
              setIsExpanded(false);
            }}
            sx={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              bgcolor:
                index === currentIndex
                  ? accentColour
                  : colourTheme.palette.text.secondary,
              cursor: "pointer",
              opacity: index === currentIndex ? 1 : 0.5,
              transition: "all 0.3s ease",
              "&:hover": {
                opacity: 1,
                transform: "scale(1.2)",
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const ManageGuestHouse = ({ userId, header }) => {
  const [guestrooms, setGuestrooms] = useState([]);
  const [guestroomsLoaded, setGuestroomsLoaded] = useState(false);
  const [searchGuestrooms, setSearchGuestrooms] = useState("");
  const [error, setError] = useState("");

  const colourTheme = useTheme();
  const { accentColour } = useColourMode();

  const handleSearchChange = (e) => {
    setSearchGuestrooms(e.target.value);
  };

  const fetchGuestrooms = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authorization token is missing.");
        return;
      }

      const response = await axios.get(`${url}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setGuestrooms(response.data);
      setGuestroomsLoaded(true);
    } catch (error) {
      console.error("Error fetching guestrooms:", error);
      setError(
        error.response?.status === 403
          ? "You don't have permission to view guestrooms."
          : "Failed to load guestrooms. Please try again later."
      );
    }
  };

  useEffect(() => {
    if (!guestroomsLoaded) {
      fetchGuestrooms();
    }
  }, [guestroomsLoaded]);

  const filteredGuestrooms = guestrooms.filter((guestroom) =>
    guestroom.type?.toLowerCase().includes(searchGuestrooms.toLowerCase())
  );

  const handleGuestroomUpdate = () => {
    fetchGuestrooms();
  };

  const handleGuestroomDelete = () => {
    fetchGuestrooms();
  };

  return (
    <Box sx={{ cursor: "pointer" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: "0.5rem",
        }}
      >
        <Box sx={{ fontSize: "1.25rem" }}>{header}</Box>
        <Box sx={{ fontSize: "0.75rem", fontWeight: "400" }}>
          {filteredGuestrooms.length}
        </Box>
      </Box>
      <Box
        sx={{
          backdropFilter: "blur(1rem)",
          boxShadow: "0 0 1rem rgba(0, 0, 0, 0.2)",
          width: "100%",
          border: `1px solid ${colourTheme.palette.text.primary}`,
          borderRadius: "1rem",
          m: "auto",
          mb: 2.5,
          p: 1,
          height: "auto",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: `1px solid ${colourTheme.palette.text.primary}`,
            padding: "0.125rem 0.25rem",
            borderRadius: "0.5rem",
          }}
        >
          <Box>
            <input
              type="text"
              value={searchGuestrooms}
              onChange={handleSearchChange}
              placeholder="Search Rooms..."
              style={{
                width: "80%",
                padding: "0.25rem",
                background: "none",
                border: "none",
                outline: "none",
                color: colourTheme.palette.text.primary,
              }}
            />
          </Box>
          <Box sx={{ display: "grid", placeItems: "center" }}>
            <SearchIcon
              style={{
                width: "0.75rem",
                height: "0.75rem",
                cursor: "pointer",
                color: colourTheme.palette.text.primary,
              }}
            />
          </Box>
        </Box>

        {error ? (
          <Box sx={{ textAlign: "center", p: 4, color: "error.main" }}>
            {error}
          </Box>
        ) : filteredGuestrooms.length > 0 ? (
          <GuestRoomCarousel
            userId={userId}
            guestrooms={filteredGuestrooms}
            onUpdate={handleGuestroomUpdate}
            onDelete={handleGuestroomDelete}
          />
        ) : (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <Typography>
              {guestroomsLoaded
                ? "No guestrooms match your search"
                : "Loading..."}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ManageGuestHouse;
